import { useState, useEffect, useRef, useCallback } from 'react';

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function DeleteAccountModal({ isLastMember, onConfirm, onClose }) {
  const [visible, setVisible] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const sheetRef = useRef(null);
  const inputRef = useRef(null);

  const canConfirm = confirmText === 'DELETE';

  // Slide in on mount
  useEffect(() => {
    sheetRef.current?.getBoundingClientRect();
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Focus input on open
  useEffect(() => {
    if (visible) inputRef.current?.focus();
  }, [visible]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape key — blocked during deletion
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && !deleting) handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleting]);

  const handleClose = useCallback(() => {
    if (deleting) return;
    if (prefersReducedMotion) { onClose(); return; }
    setVisible(false);
    setTimeout(onClose, 300);
  }, [onClose, deleting]);

  const handleDelete = async () => {
    if (!canConfirm || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch {
      setError('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Delete account">
      {/* Backdrop — blocked during deletion */}
      <div
        className="absolute inset-0 bg-black/40"
        style={{ opacity: visible ? 1 : 0, transition: prefersReducedMotion ? 'none' : 'opacity 0.3s' }}
        onClick={deleting ? undefined : handleClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-panel rounded-t-2xl overflow-y-auto overscroll-contain"
        style={{
          maxHeight: '85vh',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: prefersReducedMotion ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-panel z-10">
          <div className="w-10 h-1 bg-surface rounded-full" />
        </div>

        <div className="px-5 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-lg font-bold text-ink">Delete Account</h3>
            <button
              type="button"
              onClick={handleClose}
              disabled={deleting}
              aria-label="Close"
              className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-surface transition-colors text-ink-soft disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Warning */}
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
            <p className="font-body text-sm font-semibold text-red-700 mb-2">
              This action is permanent and cannot be undone.
            </p>
            <p className="font-body text-sm text-red-600 mb-2">The following will be deleted:</p>
            <ul className="font-body text-sm text-red-600 list-disc list-inside space-y-0.5">
              <li>Your profile and preferences</li>
              <li>Your swipe history and meal ratings</li>
              <li>Your push notification subscriptions</li>
              {isLastMember && (
                <>
                  <li>Your entire household</li>
                  <li>All household recipes</li>
                  <li>All meal plans and shopping lists</li>
                </>
              )}
            </ul>
          </div>

          {/* Confirmation input */}
          <label className="block mb-4">
            <span className="font-body text-sm text-ink-soft mb-1.5 block">
              Type <span className="font-semibold text-ink">DELETE</span> to confirm
            </span>
            <input
              ref={inputRef}
              type="text"
              value={confirmText}
              onChange={(e) => { setConfirmText(e.target.value); setError(null); }}
              disabled={deleting}
              autoComplete="off"
              spellCheck={false}
              className="w-full px-3 py-2.5 border border-hairline rounded-xl font-body text-sm text-ink bg-surface placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-red-400/30 focus:border-red-400 disabled:opacity-50"
              placeholder="DELETE"
            />
          </label>

          {/* Error banner */}
          {error && (
            <div className="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200">
              <p className="font-body text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Confirm button */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={!canConfirm || deleting}
            className="w-full py-3 bg-red-600 text-white font-body text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {deleting ? 'Deleting Account...' : 'Permanently Delete Account'}
          </button>

          {/* Cancel */}
          <button
            type="button"
            onClick={handleClose}
            disabled={deleting}
            className="w-full mt-2 py-3 bg-surface text-ink-soft font-body text-sm font-semibold rounded-xl hover:bg-surface disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>

          {/* Safe area */}
          <div className="h-safe-b" />
        </div>
      </div>
    </div>
  );
}
